import {
  Mail, Tag, Mic, Video, MessageSquare, Sparkles, PenSquare,
  FolderOpen, CalendarDays, ListTodo, Sheet, LineChart, Globe, Search, Shield, Map
} from "lucide-react";

export type RuleKind =
  | "gmail_query" | "gmail_label" | "otter_keyword"
  | "zoom_meeting_keyword" | "zoom_chat_channel" | "zoom_ai_notes" | "zoom_whiteboard"
  | "drive_folder" | "calendar_keyword" | "asana_project" | "gsheet_range" | "fred_series"
  | "firecrawl_page_diff" | "firecrawl_sitemap_diff" | "firecrawl_regulator" | "firecrawl_search";

export const RULE_KINDS: { kind: RuleKind; label: string; icon: any; desc: string; ingestFn: string; supportsRunNow: boolean; }[] = [
  { kind: "gmail_query", label: "Gmail query", icon: Mail, desc: "Match inbox using Gmail search syntax.", ingestFn: "research-ingest-gmail", supportsRunNow: true },
  { kind: "gmail_label", label: "Gmail label", icon: Tag, desc: "Pull threads tagged with a specific Gmail label.", ingestFn: "research-ingest-gmail", supportsRunNow: true },
  { kind: "otter_keyword", label: "Otter keyword", icon: Mic, desc: "Otter transcripts matching keywords.", ingestFn: "research-ingest-otter", supportsRunNow: false },
  { kind: "zoom_meeting_keyword", label: "Zoom meeting", icon: Video, desc: "Zoom recordings by meeting topic keywords.", ingestFn: "research-ingest-zoom-recording", supportsRunNow: false },
  { kind: "zoom_chat_channel", label: "Zoom chat", icon: MessageSquare, desc: "Messages from a specific Zoom Team Chat channel.", ingestFn: "research-ingest-zoom-chat", supportsRunNow: false },
  { kind: "zoom_ai_notes", label: "Zoom AI notes", icon: Sparkles, desc: "Zoom AI Companion meeting summaries.", ingestFn: "research-ingest-zoom-ai-notes", supportsRunNow: false },
  { kind: "zoom_whiteboard", label: "Zoom whiteboards", icon: PenSquare, desc: "Whiteboards from Zoom meetings.", ingestFn: "research-ingest-zoom-recording", supportsRunNow: false },
  { kind: "drive_folder", label: "Drive folder", icon: FolderOpen, desc: "New files in a Google Drive folder.", ingestFn: "research-ingest-drive", supportsRunNow: true },
  { kind: "calendar_keyword", label: "Calendar", icon: CalendarDays, desc: "Calendar events by keyword.", ingestFn: "research-ingest-calendar", supportsRunNow: false },
  { kind: "asana_project", label: "Asana project", icon: ListTodo, desc: "Tasks updated in an Asana project.", ingestFn: "research-ingest-asana", supportsRunNow: false },
  { kind: "gsheet_range", label: "Google Sheet range", icon: Sheet, desc: "A1-notation range from a Google Sheet.", ingestFn: "research-ingest-gsheet", supportsRunNow: false },
  { kind: "fred_series", label: "FRED series", icon: LineChart, desc: "FRED macro series observations.", ingestFn: "research-ingest-fred", supportsRunNow: true },
  { kind: "firecrawl_page_diff", label: "Firecrawl page diff", icon: Globe, desc: "Detect changes on a single web page.", ingestFn: "research-ingest-web", supportsRunNow: true },
  { kind: "firecrawl_sitemap_diff", label: "Firecrawl sitemap", icon: Map, desc: "Detect new/changed URLs from a sitemap.", ingestFn: "research-ingest-web", supportsRunNow: true },
  { kind: "firecrawl_regulator", label: "Firecrawl regulator", icon: Shield, desc: "Watch a regulator site for new publications.", ingestFn: "research-ingest-web", supportsRunNow: true },
  { kind: "firecrawl_search", label: "Firecrawl search", icon: Search, desc: "Run a periodic Firecrawl search query.", ingestFn: "research-ingest-web", supportsRunNow: true },
];

export const KIND_META: Record<RuleKind, typeof RULE_KINDS[number]> =
  Object.fromEntries(RULE_KINDS.map((k) => [k.kind, k])) as any;

export const DEFAULT_CONFIG: Record<RuleKind, any> = {
  gmail_query: { query: "", max_results_per_run: 50, include_attachments: true },
  gmail_label: { label: "", max_results_per_run: 50 },
  otter_keyword: { keywords: [], lookback_days: 7 },
  zoom_meeting_keyword: { keywords: [], lookback_days: 7 },
  zoom_chat_channel: { channel_name: "", lookback_hours: 24 },
  zoom_ai_notes: { lookback_days: 7 },
  zoom_whiteboard: { lookback_days: 14 },
  drive_folder: { folder_id: "", mime_types: [], lookback_days: 7 },
  calendar_keyword: { keywords: [], lookback_days: 1, lookahead_days: 7 },
  asana_project: { project_gid: "", include_completed: false, lookback_days: 7 },
  gsheet_range: { spreadsheet_id: "", sheet_name: "", range: "" },
  fred_series: { series_ids: [] },
  firecrawl_page_diff: { url: "" },
  firecrawl_sitemap_diff: { url: "" },
  firecrawl_regulator: { url: "" },
  firecrawl_search: { query: "" },
};

export function validateConfig(kind: RuleKind, cfg: any): string | null {
  if (!cfg) return "Config required";
  switch (kind) {
    case "gmail_query": if (!cfg.query?.trim()) return "Query is required";
      break;
    case "gmail_label": if (!cfg.label?.trim()) return "Label is required";
      break;
    case "zoom_chat_channel": if (!cfg.channel_name?.trim()) return "Channel name required";
      break;
    case "drive_folder": if (!cfg.folder_id?.trim()) return "Folder ID required";
      break;
    case "asana_project": if (!cfg.project_gid?.trim()) return "Project GID required";
      break;
    case "gsheet_range":
      if (!cfg.spreadsheet_id?.trim()) return "Spreadsheet ID required";
      if (!cfg.range?.trim()) return "Range required";
      break;
    case "fred_series":
      if (!Array.isArray(cfg.series_ids) || cfg.series_ids.length === 0) return "At least one series ID required";
      break;
    case "otter_keyword":
    case "zoom_meeting_keyword":
    case "calendar_keyword":
      if (!Array.isArray(cfg.keywords) || cfg.keywords.length === 0) return "At least one keyword required";
      break;
  }
  return null;
}

export function configHasPlaceholder(cfg: any): boolean {
  return JSON.stringify(cfg ?? {}).includes("PLACEHOLDER_");
}

export const DRIVE_MIME_PRESETS = [
  { value: "application/vnd.google-apps.presentation", label: "Google Slides" },
  { value: "application/vnd.google-apps.document", label: "Google Docs" },
  { value: "application/vnd.google-apps.spreadsheet", label: "Google Sheets" },
  { value: "application/pdf", label: "PDF" },
  { value: "application/vnd.openxmlformats-officedocument.presentationml.presentation", label: "PowerPoint (.pptx)" },
  { value: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", label: "Word (.docx)" },
];
